/** Presentation helpers: product coverage units vs approved test evidence corpus. */

export const COVERAGE_UNITS_EXPLANATION =
  'Coverage is calculated from product coverage units. Approved test cases are used as evidence to decide whether each unit is covered, partially covered, or missing.';

export const PARTIAL_MATCH_BANNER_TEXT =
  'Partial matches do not count as covered until match strength and required qualifiers satisfy the covered threshold. Review partial units before deciding whether more targeted tests are needed.';

export const LEGACY_EVIDENCE_UNAVAILABLE = 'Unavailable for older runs';

export const formatOptionalCount = (value) => {
  if (value == null || value === '') return '-';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '-';
  return numeric;
};

export const computeProductCoverageUnitsTotal = (summary = {}) => {
  const explicit = summary.coverage_units_total ?? summary.total_units;
  if (explicit != null && explicit !== '') {
    const numeric = Number(explicit);
    if (!Number.isNaN(numeric)) return numeric;
  }
  const covered = Number(summary.covered_units ?? 0);
  const partial = Number(summary.maybe_units ?? 0);
  const missing = Number(summary.missing_units ?? 0);
  if ([covered, partial, missing].some((n) => Number.isNaN(n))) return null;
  return covered + partial + missing;
};

export const getValidationEvidenceFromReport = (structuredReport = {}, rawReport = {}) => {
  const evidence = structuredReport.evidence
    || (rawReport.evidence && typeof rawReport.evidence === 'object' ? rawReport.evidence : {});
  const matchingMetadata = structuredReport.matching_metadata
    || (rawReport.matching_metadata && typeof rawReport.matching_metadata === 'object'
      ? rawReport.matching_metadata
      : {});
  return { evidence, matchingMetadata };
};

export const formatApprovedTestsConsidered = (evidence = {}) => {
  if (evidence.approved_tests_considered == null) {
    return { display: '-', available: false };
  }
  return {
    display: formatOptionalCount(evidence.approved_tests_considered),
    available: true,
  };
};

export const formatMatcherModeLabel = (matchingMetadata = {}) => {
  const version = matchingMetadata.matcher_version;
  const signals = matchingMetadata.signals_available;
  if (Array.isArray(signals) && signals.length > 0) {
    const signalLabel = signals.join(' + ');
    return version ? `${signalLabel} (v${version})` : signalLabel;
  }
  if (version) return `Matcher v${version}`;
  return null;
};

export const formatQualifierConfigStatus = (matchingMetadata = {}) => {
  const status = matchingMetadata.qualifier_config_status;
  if (!status) return null;
  const version = matchingMetadata.qualifier_config_version;
  return version ? `${status} (${version})` : status;
};

export const getValidationRunListFields = (run = {}) => {
  const isValidation = run.run_type === 'validation';
  const covered = isValidation ? (run.covered_units ?? 0) : null;
  const partial = isValidation ? (run.partial_units ?? 0) : null;
  const missing = isValidation ? (run.missing_units ?? 0) : null;
  const coverageUnits = isValidation
    ? (run.coverage_units ?? run.coverage_units_total ?? (covered + partial + missing))
    : null;
  const approvedTests = isValidation
    ? formatApprovedTestsConsidered({ approved_tests_considered: run.approved_tests_considered })
    : { display: '-', available: false };

  return {
    isValidation,
    coveragePercent: run.coverage_percent,
    coverageUnits,
    covered,
    partial,
    missing,
    approvedTestsDisplay: approvedTests.display,
    generatedTests: run.generated_tests ?? 0,
  };
};

export const getValidationSummaryGroups = (structuredReport = {}, rawReport = {}) => {
  const summary = structuredReport.summary || {};
  const { evidence, matchingMetadata } = getValidationEvidenceFromReport(structuredReport, rawReport);
  const productCoverageUnits = computeProductCoverageUnitsTotal(summary);
  const approved = formatApprovedTestsConsidered(evidence);
  const pending = evidence.generated_suggestions_pending;
  const matcherMode = formatMatcherModeLabel(matchingMetadata);
  const qualifierStatus = formatQualifierConfigStatus(matchingMetadata);

  return {
    productCoverage: {
      coveragePercent: summary.coverage_percent,
      coverageUnitsTotal: productCoverageUnits,
      covered: summary.covered_units ?? 0,
      partial: summary.maybe_units ?? 0,
      missing: summary.missing_units ?? 0,
    },
    testEvidence: {
      approvedTestsConsidered: approved.display,
      approvedTestsAvailable: approved.available,
      generatedSuggestionsPending: pending == null ? null : formatOptionalCount(pending),
      matcherMode,
      qualifierConfigStatus: qualifierStatus,
      corpusStatuses: evidence.corpus_statuses || [],
    },
  };
};
